import Big, { Big as BigInterface } from 'big.js';
import { DecimalInstanceError, DecimalNumberError } from './decimalServiceErrors';

type DecimalSource = number | string;
type RoundingMode = 0 | 1 | 2 | 3;
export interface Decimal {
    value: Big;
}

const DecimalService = {
    parse(source: DecimalSource) {
        const big = parseBig(source);

        return toDecimal(big);
    },

    sum(a: Decimal, b: Decimal): Decimal {
        const bigA = toBig(a);
        const bigB = toBig(b);
        const result = bigA.plus(bigB);

        return toDecimal(result);
    },

    subtract(a: Decimal, b: Decimal): Decimal {
        const bigA = toBig(a);
        const bigB = toBig(b);
        const result = bigA.minus(bigB);

        return toDecimal(result);
    },

    multiply(a: Decimal, b: Decimal): Decimal {
        const bigA = toBig(a);
        const bigB = toBig(b);
        const result = bigA.mul(bigB);

        return toDecimal(result);
    },

    divide(numerator: Decimal, denominator: Decimal): Decimal {
        const bigNumerator = toBig(numerator);
        const bigDenominator = toBig(denominator);
        const result = bigNumerator.div(bigDenominator);

        return toDecimal(result);
    },

    toString(decimal: Decimal): string {
        checkDecimalInstance(decimal);
        const { value } = decimal;

        return value.toString();
    },

    toNumber(decimal: Decimal): number {
        return parseFloat(DecimalService.toString(decimal));
    },

    isGreater(a: Decimal, b: Decimal): boolean {
        const bigA = toBig(a);
        const bigB = toBig(b);

        return bigA.gt(bigB);
    },

    isLess(a: Decimal, b: Decimal): boolean {
        const bigA = toBig(a);
        const bigB = toBig(b);

        return bigA.lt(bigB);
    },

    isGreaterOrEqual(a: Decimal, b: Decimal): boolean {
        const bigA = toBig(a);
        const bigB = toBig(b);

        return bigA.gte(bigB);
    },

    isLessOrEqual(a: Decimal, b: Decimal): boolean {
        const bigA = toBig(a);
        const bigB = toBig(b);

        return bigA.lte(bigB);
    },

    isEqual(a: Decimal, b: Decimal): boolean {
        const bigA = toBig(a);
        const bigB = toBig(b);

        return bigA.eq(bigB);
    },

    round(a: Decimal, precision: number, roundingMode: RoundingMode = 1): Decimal {
        const bigA = toBig(a);

        return toDecimal(bigA.round(precision, roundingMode));
    },
};

const toDecimal = (big: BigInterface): Decimal => ({
    value: big,
});
const parseBig = (source: DecimalSource): BigInterface => {
    try {
        return new Big(source);
    } catch {
        // @ts-expect-error $TSFixMeError: 'new' expression, whose target lacks a construct signature, implicitly has an 'any' type.
        throw new DecimalNumberError();
    }
};
const checkDecimalInstance = (decimal: Decimal) => {
    if (!decimal || !decimal.value) {
        // @ts-expect-error $TSFixMeError: 'new' expression, whose target lacks a construct signature, implicitly has an 'any' type.
        throw new DecimalInstanceError();
    }
};
const toBig = (decimal: Decimal): BigInterface => decimal.value;

export default DecimalService;
